import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/insidePages';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let smallest = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft - track.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < smallest) {
          smallest = distance;
          closest = index;
        }
      });
      setActiveIndex(closest);
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="border-y border-brand-line bg-brand-light px-4 py-5" data-id="element-96" data-mp="3ijj0bm6b4llg">
      <div className="mx-auto max-w-5xl" data-id="element-97" data-mp="12hrr701z5ir">
        <div className="mb-3 text-center" data-id="element-98" data-mp="13as9q8f4ysg">
          <p className="mb-1 font-display text-[11px] font-semibold uppercase tracking-widest text-brand-orange sm:text-sm" data-id="element-99" data-mp="3sgxch9l76o7y">
            Look Inside
          </p>
          <h2 className="mb-1 font-display text-xl font-extrabold uppercase leading-tight sm:text-3xl" data-id="element-100" data-mp="34p01fkpko07w">
            Discover What's Inside SMART HOME INSTALLER PRO™
          </h2>
          <p className="mx-auto max-w-3xl text-sm font-normal leading-snug text-brand-muted sm:text-base" data-id="element-101" data-mp="13obxpapbrh8k">
            Visual installation workflows, residential layouts, wiring and device diagrams,
            automation logic, scene architectures, project blueprints, system references and
            ready-to-use professional tools.
          </p>
        </div>

        <div className="relative" data-id="element-102" data-mp="kqixwh9zznfe">
          <ul ref={trackRef} className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2" data-id="element-103" data-mp="krk8kkn3q7wl">
            {insidePages.map(page => <li key={page.id} className="w-[86%] shrink-0 snap-center sm:w-[62%] lg:w-[48%]" data-id="element-104" data-mp="tqnppj2pkd8g">
                <figure className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-lg" data-id="element-105" data-mp="ypqx3a65uvjl">
                  <img src={page.src} alt={page.alt} loading="lazy" className="block w-full object-contain" data-id="element-106" data-mp="2562ydk4a4tje" />
                  <figcaption className="border-t border-brand-line px-3 py-2 text-center font-display text-[11px] font-semibold uppercase tracking-wide text-brand-ink sm:text-xs" data-id="element-107" data-mp="2a4e7ttqnx213">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} className="absolute left-0 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-brand-line bg-white text-brand-ink shadow-md transition-colors hover:bg-brand-light disabled:opacity-40 sm:flex" aria-label="Previous page" data-id="element-108" data-mp="2aij45s7z61cw">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-109" data-mp="1rk6xcd4jd3kh" />
          </button>
          <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === insidePages.length - 1} className="absolute right-0 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-brand-line bg-white text-brand-ink shadow-md transition-colors hover:bg-brand-light disabled:opacity-40 sm:flex" aria-label="Next page" data-id="element-110" data-mp="1rkgow558bisa">
            <ChevronRightIcon className="h-5 w-5" data-id="element-111" data-mp="3p0caarih14k3" />
          </button>
        </div>

        <div className="mt-2 flex items-center justify-center gap-2" data-id="element-112" data-mp="3txkvl0yx1opk">
          {insidePages.map((page, index) => <button key={page.id} type="button" onClick={() => scrollToIndex(index)} className={`h-2 rounded-full transition-all duration-200 ${index === activeIndex ? 'w-6 bg-brand-orange' : 'w-2 bg-brand-line'}`} aria-label={`Go to page ${index + 1}: ${page.caption}`} aria-current={index === activeIndex} data-id="element-113" data-mp="3fycu21fa64i7" data-mpi={index} />)}
        </div>
        <p className="mt-1 text-center text-[11px] font-medium text-brand-muted sm:text-xs" data-id="element-114" data-mp="3b24jl1c12zhd">
          Swipe to browse the 5 real pages from inside the system.
        </p>
      </div>
    </section>;
}